import {useCallback, useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {addListener} from '@reduxjs/toolkit';
import {Badge} from '@/components/ui/badge';
import {Button} from '@/components/app/button';
import {Checkbox} from '@/components/ui/checkbox';
import {confirm} from '@/components/app/confirmation';
import {Modal} from '@/components/app/modal';
import {useDebouncedState} from '@/app/hooks/useTimers';
import {Input} from '@/components/ui/input';
import {MenuAction} from '@/components/app/navigation';
import {LoadingSkeleton} from '@/components/app/feedback';
import {StatusBadge} from '@/components/app/feedback';
import {ContentHints} from '@/components/app/hint';
import {cn} from '@/lib/utils';
import {Step} from 'intro.js-react';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import {Icon} from '../../../app/components/Icon';
import {Steps} from '../../../app/components/Intro';
import {Form} from '../../../app/components/Form';
import {MenuButton} from '../../../app/components/MenuButton';
import {useAppSelector} from '../../../app/hooks/useAppSelector';
import {useAppDispatch} from '../../../app/hooks/useAppDispatch';
import {useDevice} from '../../../app/hooks/useDevice';
import {useLazyLoad} from '../../../app/hooks/useLazyLoad';
import {ScreenSize, useScreenSize} from '../../../app/hooks/useScreenSize';
import {useDeleteBiotwinShareMutation} from '../../../app/services/biotwinShareApi';
import {
  useCreateBiotwinMutation,
  useDeleteBiotwinMutation,
  useDuplicateBiotwinMutation,
  useGetBiotwinsQuery,
  useLazyGetBiotwinQuery,
  useLazyGetBiotwinsQuery,
  useUpdateBiotwinMutation,
} from '../../../app/services/biotwinApi';
import {
  useGetIdentitySettingsQuery,
  useRefreshTokenMutation,
  useSetIdentitySettingsMutation,
} from '../../../app/services/identitiesApi';
import {toastError, toastSuccess} from '../../../app/toast';
import {checkApiError} from '../../../app/utils/apiUtils';
import {DEFAULT_TENANT_ID, selectSession} from '../../auth/authSlice';
import {BiotwinShareManagementButton} from '../../biotwin-share/components/BiotwinShareManagementButton';
import {useBiotwinShare} from '../../biotwin-share/hooks/useBiotwinShare';
import {BiotwinShareForm} from '../../biotwin-share/components/BiotwinShareForm';
import {useSessionState} from '../../auth/useSessionState';
import {useAppEventListen} from '../../core/hooks/useAppEventListen';
import {useBlockUI} from '../../core/hooks/useBlockUI';
import {MatchMode} from '../../../app/types';
import {useAssistantUtils} from '../../core/hooks/useAssistantUtils';
import {usePageMessage} from '../../core/hooks/usePageMessage';
import {useDemo} from '../../demo/hooks/useDemo';
import {BiotwinHistory} from '../components/BiotwinHistory';
import {BiotwinInfo} from '../components/BiotwinInfo';
import {BiotwinNameForm} from '../components/BiotwinNameForm';
import {BiotwinWizard} from '../components/BiotwinWizard';
import {NoteForm} from '../components/NoteForm';
import {useReadOnly} from '../hooks/useReadOnly';
import {Biotwin} from '../types';
import {selectTenantId} from '../../auth/authSlice';
import {useGetTenantsQuery} from '../../../app/services/tenantApi';
import {InfoSection} from '../../../app/components/InfoSection';
import {useLazyGetPatientInfoQuery} from '../../../app/services/e2eApi';
import {isCompleteBiotwin} from '../helpers/utils';
import {getErrorMessage} from '../../../app/utils/miscUtils';
import {blockUI, unblockUI} from '../../core/coreSlice';
import {useExportBiotwin} from '../hooks/useExportBiotwin';
import {useImportBiotwin} from '../hooks/useImportBiotwin';
const pageSize = Math.floor(window.screen.availHeight / 51);
export const BiotwinList = () => {
  const dispatch = useAppDispatch();
  const screenSize = useScreenSize();
  const session = useAppSelector(selectSession);
  const tenantId = useAppSelector(selectTenantId);
  const navigate = useNavigate();
  const [globalFilter, globalFilterDebounced, setGlobalFilter] = useDebouncedState('', 300);
  const [getBiotwin] = useLazyGetBiotwinQuery();
  const [getBiotwins, {isFetching: isFetchingGetBiotwins}] = useLazyGetBiotwinsQuery();
  const [totalCount, setTotalCount] = useState(0);
  const [createdBy, setCreatedBy] = useState<number | undefined>();
  const {data: exactMatchedBiotwins, isFetching: isFetchingExactMatchedBiotwins} = useGetBiotwinsQuery(
    {
      name: globalFilterDebounced,
      nameMatchMode: MatchMode.Exact,
    },
    {
      skip: !session?.id || globalFilterDebounced === '',
    },
  );
  const lastResult = useRef<ReturnType<typeof getBiotwins>>(undefined);
  const {
    isLoading: isLoadingBiotwins,
    isLoadingMore: isLoadingMoreBiotwins,
    isInitialized: isInitializedBiotwins,
    isRefreshing: isRefreshingBiotwins,
    items: biotwins,
    loadMore: loadMoreBiotwins,
    refresh: refreshBiotwins,
  } = useLazyLoad<Biotwin>({
    getItems: useCallback(
      async ({pageNumber, pageSize, trigger}) => {
        if (lastResult.current) {
          lastResult.current.abort();
          lastResult.current.unsubscribe();
          lastResult.current = undefined;
        }
        await new Promise((resolve) => setTimeout(resolve, 10));
        lastResult.current = getBiotwins(
          {
            createdBy,
            name: `^(?!__).*${globalFilterDebounced}.*`,
            nameMatchMode: MatchMode.Regex,
            pageNumber,
            pageSize,
            tenantId,
          },
          trigger === 'soft-refresh',
        );
        return lastResult.current
          .unwrap()
          .then((data) => {
            if (lastResult.current) {
              setTotalCount(data.totalCount);
            }
            return data.items;
          })
          .finally(() => {
            lastResult.current?.unsubscribe();
            lastResult.current = undefined;
          });
      },
      [createdBy, globalFilterDebounced, tenantId],
    ),
    pageSize,
    skip: !session?.id,
  });
  const [createBiotwinDialogVisible, setCreateBiotwinDialogVisible] = useState(false);
  const [createBiotwinExistingName, setCreateBiotwinExistingName] = useState('Test 1');
  const [biotwinWizardDialogVisible, setBiotwinWizardDialogVisible] = useState(false);
  const [activeBiotwinId, setActiveBiotwinId] = useState<number>();
  const activeBiotwin = biotwins?.find((biotwin) => biotwin.id === activeBiotwinId);
  const [duplicateDialogVisible, setDuplicateDialogVisible] = useState(false);
  const [editNameDialogVisible, setEditNameDialogVisible] = useState(false);
  const [infoDialogVisible, setInfoDialogVisible] = useState(false);
  const [noteFormDialogVisible, setNoteFormDialogVisible] = useState(false);
  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [forcedBiotwinWizardDialogVisible, setForcedBiotwinWizardDialogVisible] = useState(false);
  const [summaryDialogVisible, setSummaryDialogVisible] = useState(false);
  const {data: identitySettings} = useGetIdentitySettingsQuery();
  const [createBiotwin, {isLoading: isLoadingCreateBiotwin}] = useCreateBiotwinMutation();
  const [deleteBiotwin, {isLoading: isLoadingDeleteBiotwin}] = useDeleteBiotwinMutation();
  const [duplicateBiotwin, {isLoading: isLoadingDuplicateBiotwin}] = useDuplicateBiotwinMutation();
  const [updateBiotwin, {isLoading: isLoadingUpdateBiotwin}] = useUpdateBiotwinMutation();
  const [setIdentitySettings, {isLoading: isLoadingSetIdentitySettings}] = useSetIdentitySettingsMutation();
  const [deleteBiotwinShare, {isLoading: isLoadingDeleteBiotwinShare}] = useDeleteBiotwinShareMutation();
  const [introDisplayed, setIntroDisplayed] = useSessionState('intro.biotypeList.displayed');
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const [settingsDialogVisible, setSettingsDialogVisible] = useState(false);
  const {getBiotwinShares, isIncoming} = useBiotwinShare();
  const {pushAssistantContent, removeAssistantContent} = useAssistantUtils();
  const {isDemo, showLimitReachedDialog} = useDemo();
  const {isReadOnly} = useReadOnly();
  const {isMobile} = useDevice();
  const [menuOwnerBiotwin, setMenuOwnerBiotwin] = useState<Biotwin>();
  const {exportBiotwin} = useExportBiotwin();
  const {importBiotwin} = useImportBiotwin();
  useEffect(() => {
    if (isMobile) {
      const listener = () => {
        if (
          document.documentElement.scrollHeight - document.documentElement.scrollTop ===
          document.documentElement.clientHeight
        ) {
          void loadMoreBiotwins();
        }
      };
      document.addEventListener('scroll', listener);
      return () => {
        document.removeEventListener('scroll', listener);
      };
    }
  }, [isMobile, loadMoreBiotwins]);
  useEffect(() => {
    if (!isDemo && isInitializedBiotwins && !introDisplayed) {
      setStepsEnabled(true);
    }
  }, [!introDisplayed, isDemo, isInitializedBiotwins]);
  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        predicate: (action) => {
          return (
            action.type === 'api/invalidateTags' &&
            action.payload instanceof Array &&
            action.payload.some(
              (item) =>
                isEqual(item, 'Biotwins') ||
                isEqual(item, {
                  type: 'Biotwins',
                  id: 'LIST',
                }),
            )
          );
        },
        effect: () => {
          setTimeout(() => {
            void refreshBiotwins();
          }, 0);
        },
      }),
    );
    return () => {
      // @ts-expect-error - TS doesn't know about the unsubscribe method
      unsubscribe();
    };
  }, [dispatch, refreshBiotwins]);
  useAppEventListen(
    'help',
    useCallback(() => {
      if (!isDemo) {
        setStepsEnabled(true);
      }
    }, [isDemo]),
  );
  useBlockUI(
    isLoadingCreateBiotwin ||
      isLoadingDeleteBiotwin ||
      isLoadingDeleteBiotwinShare ||
      isLoadingDuplicateBiotwin ||
      isLoadingUpdateBiotwin ||
      isLoadingSetIdentitySettings ||
      isRefreshingBiotwins,
  );
  usePageMessage(
    {
      severity: 'info',
      content: (
        <span>
          You are using free plan of Biotandem, you can create up to 5 biotwins on the Family plan.&nbsp;
          <Link className="text-blue-800 font-bold underline" to="/subscription">
            Upgrade now!
          </Link>
        </span>
      ),
    },
    [],
    session?.subscriptionType === 'Free',
  );
  useEffect(() => {
    if (isDemo && isInitializedBiotwins) {
      return pushAssistantContent({
        detail:
          'Biotwins include key features to consider when diagnosing and deciding on treatments for an individual.',
        detailDelay: 1000,
        summary: 'This is your Biotwin List.',
      });
    }
  }, [isDemo, isInitializedBiotwins]);
  if (!session) {
    return null;
  }
  return (
    <div className={'page-container p-0 pt-4 h-full sm:p-6'}>
      <ContentHints target=".last-modified" />
      {!isInitializedBiotwins && isLoadingBiotwins && (
        <div className={'bg-white h-full sm:rounded-xl sm:shadow-md'}>
          <div className={'p-4 border-b border-border'}>
            <LoadingSkeleton height="2rem" width="5rem" />
          </div>
          <div className={'flex flex-col gap-4 p-4'}>
            <LoadingSkeleton height="2rem" width="100%" />
            <LoadingSkeleton height="2rem" width="100%" />
            <LoadingSkeleton height="2rem" width="100%" />
          </div>
        </div>
      )}
      {isInitializedBiotwins && (
        <section className={'flex flex-col bg-white h-full sm:rounded-xl sm:shadow-md'}>
          <header className={'flex flex-wrap items-center gap-2 px-4 py-3 border-b border-border sm:gap-4'}>
            <h2 className={'text-base leading-none whitespace-nowrap sm:py-4 my-0'} data-name={'biotwin-list-title'}>
              Biotwin List
            </h2>
            <StatusBadge tone="warning">{totalCount}</StatusBadge>
            <Button
              className={cn('mr-auto', 'rounded-full')}
              onClick={() => setSettingsDialogVisible(true)}
              variant="text"
              type="button"
              startIcon={<Icon icon={'cog'} />}
              aria-label={'cog'}
              size="icon"
            />
            {session?.isAdmin === 'true' && (
              <div className={'order-last flex w-full items-center gap-2 sm:order-none sm:w-auto sm:mr-4'}>
                <Checkbox
                  checked={createdBy != null}
                  id={'only-owned-biotwins-checkbox'}
                  onCheckedChange={(e) => setCreatedBy(e ? session?.id : undefined)}
                />
                <label className={'text-muted-foreground'} htmlFor={'only-owned-biotwins-checkbox'}>
                  Show my biotwins
                </label>
              </div>
            )}
            <div className="flex shrink-0 items-center gap-2" data-name={'biotwin-list-add-button-group'}>
              <Button
                aria-label="Add a biotwin"
                onClick={() => {
                  removeAssistantContent('add-a-biotwin');
                  if (isDemo && biotwins.filter((biotwin) => !biotwin.isDemo).length > 0) {
                    showLimitReachedDialog({
                      message: 'Register right away to add more biotwins.',
                      warning: 'You can add only one biotwin in demo.',
                    });
                  } else {
                    setCreateBiotwinExistingName('');
                    setCreateBiotwinDialogVisible(true);
                  }
                }}
                onMouseEnter={() => {
                  pushAssistantContent({
                    detail: 'This button allows you to add a new biotwin to your list.',
                    id: 'add-a-biotwin',
                    summary: 'Add a biotwin',
                  });
                }}
                onMouseLeave={() => {
                  removeAssistantContent('add-a-biotwin');
                }}
                hint={screenSize === ScreenSize.ExtraSmall ? 'Add a biotwin' : undefined}
                className="rounded-full"
                type="button"
                startIcon={<Icon icon={'plus'} />}>
                {screenSize > ScreenSize.Small ? 'Add a biotwin' : undefined}
              </Button>
              {session?.isAdmin === 'true' && (
                <Button
                  className="rounded-full"
                  onClick={async () => {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.accept = 'application/json';
                    fileInput.onchange = async () => {
                      if (fileInput.files && fileInput.files.length > 0) {
                        const file = fileInput.files[0];
                        try {
                          dispatch(blockUI());
                          const fileContent = await file.text();
                          const json = JSON.parse(fileContent);
                          await importBiotwin(json, tenantId);
                          toastSuccess('Biotwin imported successfully');
                        } catch (e) {
                          console.error('Error importing biotwin:', e);
                          toastError('Failed to import biotwin');
                        } finally {
                          dispatch(unblockUI());
                        }
                      }
                    };
                    fileInput.click();
                  }}
                  hint="Import biotwin"
                  type="button"
                  startIcon={<Icon icon={'file-import'} />}
                  aria-label="Import biotwin"
                  size="icon"
                />
              )}
              <Button
                className="rounded-full"
                onClick={() => {
                  removeAssistantContent('add-a-biotwin-via-wizard');
                  if (isDemo && biotwins.filter((biotwin) => !biotwin.isDemo).length > 1) {
                    showLimitReachedDialog({
                      message: 'Register right away to add more biotwins.',
                      warning: 'You can add only one biotwin in demo.',
                    });
                  } else {
                    setBiotwinWizardDialogVisible(true);
                  }
                }}
                onMouseEnter={() => {
                  pushAssistantContent({
                    detail: 'The wizard will guide you through a series of steps.',
                    id: 'add-a-biotwin-via-wizard',
                    summary: 'Add a biotwin via wizard',
                  });
                }}
                onMouseLeave={() => {
                  removeAssistantContent('add-a-biotwin-via-wizard');
                }}
                hint="Add a biotwin via wizard"
                type="button"
                startIcon={<Icon icon={'magic'} />}
                aria-label="Add a biotwin via wizard"
                size="icon"
              />
            </div>
          </header>
          <div className={'relative'}>
            <div className={'absolute top-1/2 -translate-y-1/2 left-0 ml-4'}>
              <Icon
                className={'text-muted-foreground'}
                icon={isInitializedBiotwins && isLoadingBiotwins ? 'spinner' : 'search'}
                spin={isInitializedBiotwins && isLoadingBiotwins}
                style={{
                  marginTop: -1,
                }}
              />
            </div>
            <Input
              className={'w-full rounded-none border-t-0 border-x-0 shadow-none py-4 pr-4'}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search"
              style={{
                paddingLeft: '2.75rem',
              }}
              value={globalFilter}
            />
          </div>
          {globalFilter !== '' &&
            !isFetchingGetBiotwins &&
            !isFetchingExactMatchedBiotwins &&
            exactMatchedBiotwins != null &&
            exactMatchedBiotwins.totalCount === 0 &&
            biotwins.length > 0 && <NoExactMatchedBiotwinsFound className={'p-2'} globalFilter={globalFilter} />}
          <div
            className={'flex-1 overflow-auto'}
            onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (target.scrollHeight - target.scrollTop <= target.clientHeight + 3) {
                void loadMoreBiotwins();
              }
            }}>
            {globalFilter !== '' && biotwins.length === 0 && (
              <NoBiotwinsFound className={'h-full flex-1'} globalFilter={globalFilter} />
            )}
            {(globalFilter === '' || biotwins.length > 0) && (
              <div className="grid min-h-0 p-4 sm:grid-cols-2 md:grid-cols-3 gap-4 lg:flex lg:flex-col lg:gap-0 lg:p-0">
                <div className={'hidden sticky top-0 bg-white z-1 font-bold border-b border-zinc-300 lg:flex'}>
                  <div className={'p-4 w-[20rem] xl:w-[24rem]'}>Name</div>
                  <div className={'p-4 w-[12rem]'}>Last Viewed</div>
                  <div className={'p-4 flex-1'}>Note</div>
                </div>
                {biotwins.map((biotwin) => {
                  return (
                    <section
                      className={cn(
                        'biotwin relative border flex flex-col gap-2 rounded-lg p-4 h-[12rem] lg:flex-row lg:items-center lg:gap-0 lg:rounded-none lg:border-x-0 lg:border-t-0 lg:h-auto lg:p-0',
                        {
                          'border-border hover:bg-muted/50':
                            !biotwin.isDemo && !biotwin.isSample && !isIncoming(biotwin),
                          'demo-biotwin bg-orange-100 border-orange-300': biotwin.isDemo,
                          'sample-biotwin bg-yellow-100 border-yellow-300': biotwin.isSample,
                          'shared-biotwin bg-blue-100 border-blue-300': isIncoming(biotwin),
                        },
                      )}
                      key={biotwin.id}>
                      <div className={'lg:w-[20rem] lg:p-4 xl:w-[24rem] truncate'}>
                        <Link to={`/biotwins/${biotwin.id}`}>{biotwin.name}</Link>
                      </div>
                      <div className={'text-muted-foreground lg:w-[12rem] lg:p-4 lg:text-foreground'}>
                        {!biotwin.isSample && (
                          <div className="inline-block last-modified" data-hint={formatCreatedOrModifiedAt(biotwin)}>
                            {fromNowCreatedOrModifiedAt(biotwin)}
                          </div>
                        )}
                      </div>
                      <div className={'lg:px-4 lg:py-1 lg:flex-1 lg:flex lg:items-center lg:min-w-0'}>
                        {biotwin.isDemo && (
                          <StatusBadge className="px-2" tone="warning">
                            {'Demo'}
                          </StatusBadge>
                        )}
                        {biotwin.isSample && (
                          <StatusBadge className="px-2" tone="success">
                            {'Sample'}
                          </StatusBadge>
                        )}
                        {isIncoming(biotwin) && (
                          <StatusBadge className="px-2" tone="info">
                            {'Shared'}
                          </StatusBadge>
                        )}
                        {!biotwin.isDemo && !biotwin.isSample && !isIncoming(biotwin) && biotwin.note && (
                          <div
                            className={'text-sm lg:text-base lg:whitespace-nowrap lg:overflow-hidden lg:text-ellipsis'}
                            id={`biotwin-${biotwin.id}-note`}>
                            <span>{biotwin.note}</span>
                            <ContentHints
                              shouldShow={(e) => {
                                if (e.target instanceof HTMLElement) {
                                  const child = e.target.querySelector('span');
                                  if (child instanceof HTMLElement) {
                                    const {width: childWidth} = child.getBoundingClientRect();
                                    const {width: targetWidth} = e.target.getBoundingClientRect();
                                    if (childWidth <= targetWidth) {
                                      return false;
                                    }
                                  }
                                }
                              }}
                              side="left"
                              target={`#biotwin-${biotwin.id}-note`}>
                              {biotwin.note}
                            </ContentHints>
                          </div>
                        )}
                      </div>
                      <div className={'flex items-center gap-4 mt-auto lg:mt-0 lg:gap-2 lg:px-4 lg:ml-auto'}>
                        <BiotwinShareManagementButton biotwinId={biotwin.id} type="button" />
                        {!biotwin.isSample && !!biotwin.biobookUpdatePhraseIds?.length && (
                          <Button
                            className="rounded-full"
                            variant="destructive-ghost"
                            onClick={() => navigate(`/biotwins/${biotwin.id}/updates`)}
                            hint={`Show ${biotwin.biobookUpdatePhraseIds.length} Updates`}
                            type="button"
                            startIcon={<Icon icon={'file-earmark-plus-fill'} />}
                            aria-label={'file earmark plus fill'}
                            size="icon"
                          />
                        )}
                        {renderBiotwinMenuButton(biotwin)}
                        <Button
                          className="grow rounded-full whitespace-nowrap lg:grow-0"
                          variant="text"
                          onClick={() => {
                            removeAssistantContent('view-biotwin');
                            navigate(`/biotwins/${biotwin.id}`);
                          }}
                          onMouseEnter={() => {
                            pushAssistantContent({
                              detail:
                                'This button will take you to the page where you can add or remove features from that biotwin.',
                              id: 'view-biotwin',
                              summary: 'View Biotwin',
                            });
                          }}
                          onMouseLeave={() => {
                            removeAssistantContent('view-biotwin');
                          }}
                          type="button"
                          endIcon={<Icon icon={'arrow-circle-right'} />}>
                          {'View Biotwin'}
                        </Button>
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
            <div
              className={'flex items-center justify-center transition-all ease-linear duration-200 overflow-hidden'}
              style={{
                height: isLoadingMoreBiotwins ? '2rem' : 0,
              }}>
              {isLoadingMoreBiotwins && <Icon icon={'spinner'} spin />}
            </div>
          </div>
        </section>
      )}

      <Modal
        className="w-[30rem]"
        title="Add a biotwin"
        onClose={() => setCreateBiotwinDialogVisible(false)}
        open={createBiotwinDialogVisible}>
        {createBiotwinExistingName && (
          <>
            <div className={'mb-6 pb-6 border-gray-400 border-b text-center'}>
              <p className={'mt-0 mb-4'}>
                <strong>{createBiotwinExistingName}</strong> already exists.
              </p>
              <Button
                className={cn('w-full', 'rounded-full')}
                isLoading={isFetchingGetBiotwins}
                onClick={async () => {
                  try {
                    const result = await getBiotwins({
                      name: createBiotwinExistingName,
                      tenantId,
                      pageNumber: 1,
                      pageSize: 1,
                    }).unwrap();
                    const existingBiotwin = result.items[0];
                    navigate(`/biotwins/${existingBiotwin.id}`);
                  } catch (e) {
                    toastError(e);
                  }
                }}
                type={'button'}>{`View ${createBiotwinExistingName}`}</Button>
            </div>
            <p className={'text-center'}>... or try another name.</p>
          </>
        )}
        <BiotwinNameForm
          onSubmit={async ({name}) => {
            try {
              await createBiotwin({
                name,
                tenantId,
              }).unwrap();
              toastSuccess('Biotwin is created successfully.');
              setCreateBiotwinDialogVisible(false);
            } catch (e) {
              if (checkApiError(e, '0154') || checkApiError(e, '0155')) {
                setCreateBiotwinExistingName(name);
              } else if (checkApiError(e, '0078')) {
                if (isDemo) {
                  showLimitReachedDialog({
                    message: 'Register right away to add more biotwins.',
                    warning: 'You cannot add more biotwins in demo.',
                  });
                } else {
                  confirm({
                    onConfirm: () => navigate('/profile/membership'),
                    confirmClassName: 'rounded-full',
                    confirmIcon: 'arrow-up',
                    confirmLabel: 'Upgrade Now',
                    title: 'Biotwin Limit Reached',
                    icon: 'exclamation-triangle',
                    message:
                      'You have reached the biotwin limit. Please upgrade your subscription in order to create more biotwins.',
                    cancelClassName: 'rounded-full bg-transparent text-muted-foreground',
                    cancelLabel: 'Maybe later',
                  });
                }
                setCreateBiotwinDialogVisible(false);
              } else {
                toastError(e);
                setCreateBiotwinDialogVisible(false);
              }
            }
          }}
        />
      </Modal>

      <BiotwinWizard
        onCancel={() => {
          void refreshBiotwins();
          setBiotwinWizardDialogVisible(false);
        }}
        onFinish={() => {
          void refreshBiotwins();
          setBiotwinWizardDialogVisible(false);
        }}
        onViewBiotwin={({biotwin}) => {
          setBiotwinWizardDialogVisible(false);
          navigate(`/biotwins/${biotwin.id}`);
        }}
        tenantId={tenantId}
        visible={biotwinWizardDialogVisible}
      />

      <Modal
        className={'w-[30rem]'}
        title={'Biotwin Info'}
        onClose={() => setInfoDialogVisible(false)}
        open={infoDialogVisible}>
        {activeBiotwin && <BiotwinInfo biotwin={activeBiotwin} />}
      </Modal>

      <Modal
        className="w-[30rem]"
        title="Edit Name"
        onClose={() => setEditNameDialogVisible(false)}
        open={editNameDialogVisible}>
        <BiotwinNameForm
          defaultValues={activeBiotwin ?? undefined}
          onSubmit={async ({name}) => {
            if (!activeBiotwin) {
              return;
            }
            try {
              await updateBiotwin({
                id: activeBiotwin.id,
                changes: {
                  name,
                },
              }).unwrap();
              toastSuccess('Biotwin is updated successfully.');
            } catch (e) {
              toastError(e);
            } finally {
              setEditNameDialogVisible(false);
            }
          }}
        />
      </Modal>

      <Modal
        className="w-[30rem]"
        title={
          <div className="flex items-center gap-2 whitespace-nowrap min-w-0 pr-4">
            {activeBiotwin?.note ? 'Edit' : 'Add'} Note
            <Badge className="whitespace-nowrap overflow-hidden text-ellipsis flex-1 min-w-0">
              {activeBiotwin?.name}
            </Badge>
          </div>
        }
        onClose={() => setNoteFormDialogVisible(false)}
        open={noteFormDialogVisible}>
        <NoteForm
          defaultValues={{
            text: activeBiotwin?.note,
          }}
          onSubmit={async ({text}) => {
            if (!activeBiotwin) {
              return;
            }
            try {
              await updateBiotwin({
                id: activeBiotwin.id,
                changes: {
                  note: text,
                },
              }).unwrap();
              toastSuccess(`Note is ${activeBiotwin?.note ? 'updated' : 'added'} successfully.`);
            } catch (e) {
              toastError(e);
            } finally {
              setNoteFormDialogVisible(false);
            }
          }}
        />
      </Modal>

      <BiotwinWizard
        forcedBiotwinBasicsMessage={`It's necessary to fill up the basics before sharing the biotwin.`}
        initialBiotwin={activeBiotwin}
        initialStep={3}
        noNewBiotwin
        noViewBiotwin
        onCancel={({biotwin}) => {
          setForcedBiotwinWizardDialogVisible(false);
          if (biotwin?.age != null) {
            setShareDialogVisible(true);
          }
        }}
        onFinish={() => {
          setForcedBiotwinWizardDialogVisible(false);
          setShareDialogVisible(true);
        }}
        onViewBiotwin={() => {}}
        startingStep={3}
        summaryStep={3}
        tenantId={tenantId}
        visible={forcedBiotwinWizardDialogVisible}
      />

      <Modal
        className={'w-[30rem]'}
        title={`Share ${activeBiotwin?.name}`}
        onClose={() => setShareDialogVisible(false)}
        open={shareDialogVisible}>
        {activeBiotwin != null && (
          <BiotwinShareForm
            biotwinId={activeBiotwin.id}
            onSubmitSuccess={() => {
              toastSuccess('Biotwin is shared successfully.');
              setShareDialogVisible(false);
            }}
          />
        )}
      </Modal>

      <Modal
        className={'h-full'}
        title={`${activeBiotwin?.name}'s Timeline`}
        onClose={() => setSummaryDialogVisible(false)}
        style={{
          maxWidth: '60rem',
          width: 'calc(100% - 3rem)',
        }}
        open={summaryDialogVisible}>
        {activeBiotwin && <BiotwinHistory userId={activeBiotwin.id} />}
      </Modal>

      <Modal
        className="w-[30rem]"
        title={`Duplicate ${activeBiotwin?.name}`}
        onClose={() => setDuplicateDialogVisible(false)}
        open={duplicateDialogVisible}>
        <BiotwinNameForm
          defaultValues={activeBiotwin ?? undefined}
          onSubmit={async ({name}) => {
            if (!activeBiotwin) {
              return;
            }
            try {
              await duplicateBiotwin({
                id: activeBiotwin.id,
                name,
              }).unwrap();
              toastSuccess('Biotwin is duplicated successfully.');
            } catch (e) {
              toastError(e);
            } finally {
              setDuplicateDialogVisible(false);
            }
          }}
        />
      </Modal>

      <Modal
        className={'w-[30rem]'}
        title={'Settings'}
        onClose={() => setSettingsDialogVisible(false)}
        open={settingsDialogVisible}>
        <Form
          className={'py-4'}
          defaultValues={identitySettings}
          fields={({reset, setValue}) => [
            {
              label: 'Show sample biotwins',
              name: 'showSamples',
              type: 'checkbox',
              typeOptions: {
                onCheckedChange: (e) => {
                  if (isDemo) {
                    showLimitReachedDialog({});
                  } else {
                    setValue('showSamples', !!e);
                    setIdentitySettings({
                      ...identitySettings,
                      showSamples: !!e,
                    })
                      .unwrap()
                      .then((updatedIdentitySettings) => {
                        reset(updatedIdentitySettings);
                        void refreshBiotwins();
                      })
                      .catch((e) => {
                        toastError(e);
                        reset(identitySettings);
                      });
                  }
                },
              },
            },
          ]}
          submitButton={false}
        />
      </Modal>

      <Steps
        enabled={stepsEnabled}
        steps={steps}
        initialStep={0}
        onExit={() => {
          document.body.classList.remove('introjs-steps-enabled');
          if (stepsEnabled) {
            if (!introDisplayed) {
              setIntroDisplayed(true);
            }
            setStepsEnabled(false);
          }
        }}
        onStart={() => document.body.classList.add('introjs-steps-enabled')}
        options={{
          hideNext: false,
          overlayOpacity: 1,
        }}
      />

      <ContentHints
        key={menuOwnerBiotwin?.id}
        shouldShow={() => {
          if (tenantId === DEFAULT_TENANT_ID) {
            return false;
          }
          return menuOwnerBiotwin != null && menuOwnerBiotwin.createdBy !== Number(session?.id);
        }}
        side="left"
        style={{
          maxWidth: '12rem',
        }}
        target={`.delete-biotwin-menu-item`}>
        Cannot delete this biotwin because it is created by another user.
      </ContentHints>
    </div>
  );
  function renderBiotwinMenuButton(biotwin: Biotwin) {
    const readOnly = isReadOnly(biotwin);
    const incoming = isIncoming(biotwin);
    const menuModel: MenuAction[] = [];
    menuModel.push({
      onSelect: () => {
        setActiveBiotwinId(biotwin.id);
        setEditNameDialogVisible(true);
      },
      icon: 'pencil',
      label: 'Edit Name',
      visible: !readOnly && biotwin.id !== session?.id,
    });
    menuModel.push({
      onSelect: () => {
        setActiveBiotwinId(biotwin.id);
        setNoteFormDialogVisible(true);
      },
      icon: 'file-edit',
      label: biotwin.note ? 'Edit Note' : 'Add Note',
      visible: !readOnly,
    });
    menuModel.push({
      onSelect: async () => {
        try {
          dispatch(blockUI());
          const fullBiotwin = await getBiotwin(
            {
              id: biotwin.id,
            },
            true,
          ).unwrap();
          setActiveBiotwinId(biotwin.id);
          if (!isCompleteBiotwin(fullBiotwin)) {
            setForcedBiotwinWizardDialogVisible(true);
          } else {
            setShareDialogVisible(true);
          }
        } catch (e) {
          toastError('Cannot share the biotwin.');
        } finally {
          dispatch(unblockUI());
        }
      },
      icon: 'share-alt',
      label: 'Share',
      visible: !readOnly && biotwin.createdBy === Number(session?.id),
    });
    menuModel.push({
      onSelect: () => {
        setActiveBiotwinId(biotwin.id);
        setDuplicateDialogVisible(true);
      },
      icon: 'clone',
      label: 'Duplicate',
    });
    menuModel.push({
      separator: !biotwin.isDemo && !biotwin.isSample,
      visible: !biotwin.isDemo && !biotwin.isSample,
    });
    menuModel.push({
      onSelect: () => {
        setActiveBiotwinId(biotwin.id);
        setSummaryDialogVisible(true);
      },
      icon: 'history',
      label: 'Show timeline',
      visible: !biotwin.isDemo && !biotwin.isSample,
    });
    menuModel.push({
      onSelect: () => {
        setActiveBiotwinId(biotwin.id);
        setInfoDialogVisible(true);
      },
      icon: 'info-circle',
      label: 'Show info',
      visible: !biotwin.isDemo && !biotwin.isSample,
    });
    menuModel.push({
      separator: true,
      visible: session?.isAdmin === 'true',
    });
    menuModel.push({
      onSelect: async () => {
        try {
          dispatch(blockUI());
          await exportBiotwin(biotwin.id);
        } catch (e) {
          toastError('Cannot export the biotwin.');
        } finally {
          dispatch(unblockUI());
        }
      },
      icon: 'file-export',
      label: 'Export',
    });
    const deleteMenuItemVisible = !readOnly && biotwin.id !== session?.id && !incoming;
    menuModel.push({
      separator: deleteMenuItemVisible,
      visible: deleteMenuItemVisible,
    });
    menuModel.push({
      className: `delete-biotwin-menu-item`,
      onSelect: () => {
        confirm({
          title: 'Delete Biotwin',
          icon: 'exclamation-triangle',
          message: 'Are you sure want to delete this biotwin?',
          onConfirm: async () => {
            try {
              await deleteBiotwin({
                id: biotwin.id,
              }).unwrap();
              toastSuccess('Biotwin is deleted successfully.');
            } catch (e) {
              toastError(e);
            }
          },
          confirmVariant: 'destructive',
          confirmClassName: 'rounded-full',
          onCancel: () => {},
          cancelClassName: 'bg-transparent rounded-full',
        });
      },
      disabled: tenantId !== DEFAULT_TENANT_ID && biotwin.createdBy !== Number(session?.id),
      icon: 'trash',
      label: 'Delete',
      visible: deleteMenuItemVisible,
    });
    menuModel.push({
      separator: incoming,
      visible: incoming,
    });
    menuModel.push({
      onSelect: () => {
        confirm({
          title: 'Remove Biotwin',
          icon: 'exclamation-triangle',
          message: 'Are you sure want to remove this biotwin?',
          onConfirm: async () => {
            const [biotwinShare] = getBiotwinShares(biotwin);
            if (!biotwinShare) {
              return;
            }
            try {
              await deleteBiotwinShare({
                id: biotwinShare.id,
              }).unwrap();
              await refreshBiotwins();
              toastSuccess('Biotwin is removed successfully.');
            } catch (e) {
              toastError(e);
            }
          },
          confirmVariant: 'destructive',
          confirmClassName: 'rounded-full',
          onCancel: () => {},
          cancelClassName: 'bg-transparent rounded-full',
        });
      },
      icon: 'trash',
      label: 'Remove',
      visible: incoming,
    });
    return (
      <MenuButton
        className={cn('absolute top-0 right-0 lg:static shrink-0 biotwin-menu-button', 'rounded-full')}
        data-name={'biotwin-operations'}
        menuClassName={cn({
          // Keep the menu above the intro.js overlay during the tour.
          'z-[2147483647]!': stepsEnabled,
        })}
        actions={menuModel}
        menuOnHide={() => setMenuOwnerBiotwin(undefined)}
        menuOnShow={() => setMenuOwnerBiotwin(biotwin)}
        onMouseEnter={() => {
          pushAssistantContent({
            detail: 'You can edit biotwin name, add notes or delete by using this menu.',
            id: 'biotwin-menu-button',
            summary: 'Biotwin action menu',
          });
        }}
        onMouseLeave={() => {
          removeAssistantContent('biotwin-menu-button');
        }}
        style={{
          margin: 2,
        }}
        variant="text"
        type="button"
        startIcon={<Icon icon={'ellipsis-h'} />}
        aria-label={'More actions'}
        size="icon"
      />
    );
  }
  function formatCreatedOrModifiedAt(biotwin: Biotwin) {
    return new Date(biotwin.createdOrModifiedAt).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  }
  function fromNowCreatedOrModifiedAt(biotwin: Biotwin) {
    return moment(biotwin.createdOrModifiedAt).fromNow();
  }
};
const steps: Step[] = [
  {
    element: '[data-name="biotwin-list-title"]',
    intro: (
      <>
        <p className="mt-0">This is where your biotwins are listed.</p>
        <p className="mb-0">
          Biotwin is the sum of features that physicians should consider when deciding on an individual.
        </p>
      </>
    ),
    position: 'top',
    title: 'Biotwin List',
  },
  {
    element: '[data-name="biotwin-list-add-button-group"]',
    intro: 'You can add more biotwins to your list. The wizard will guide you through a series of steps.',
    position: 'top',
    title: 'Add a Biotwin',
  },
  {
    element: '[data-name="biotwin-operations"]',
    intro: `You can edit biotwin's name, add notes or delete by using this menu.`,
    position: 'left',
    title: 'Biotwin Operations',
  },
];
interface NoExactMatchedBiotwinsFoundProps {
  className?: string;
  globalFilter: string;
}
const NoExactMatchedBiotwinsFound = ({className, globalFilter}: NoExactMatchedBiotwinsFoundProps) => {
  const tenantId = useAppSelector(selectTenantId);
  const {data: tenants = []} = useGetTenantsQuery(undefined, {
    skip: !tenantId || tenantId === 1,
  });
  const tenant = tenants.find((tenant) => tenant.id === tenantId);
  const usesIntegration = tenant?.usesIntegration;
  const [prevGlobalFilter, setPrevGlobalFilter] = useState(globalFilter);
  const [cannotGetFromHIS, setCannotGetFromHIS] = useState(false);
  const [getPatientInfo, {error: errorGetPatientInfo, isFetching: isFetchingGetPatientInfo}] =
    useLazyGetPatientInfoQuery();
  const navigate = useNavigate();
  if (prevGlobalFilter !== globalFilter) {
    setPrevGlobalFilter(globalFilter);
    setCannotGetFromHIS(false);
  }
  if (!usesIntegration) {
    return null;
  }
  return (
    <div className={className}>
      <Button
        className={cn('w-[12rem]', 'rounded-full')}
        disabled={cannotGetFromHIS}
        isLoading={isFetchingGetPatientInfo}
        onClick={async () => {
          try {
            const patientInfo = await getPatientInfo({
              patientId: globalFilter,
            }).unwrap();
            if (patientInfo.biotypeId) {
              navigate(`/biotwins/${patientInfo.biotypeId}`);
            }
          } catch (e) {
            setCannotGetFromHIS(true);
          }
        }}
        type="button"
        startIcon={<Icon icon={'download'} />}>
        {'Create From HIS'}
      </Button>
      {cannotGetFromHIS && (
        <div
          className={'absolute top-full left-1/2 flex justify-center mt-2 flex items-center whitespace-nowrap'}
          style={{
            width: 0,
          }}>
          <span className={'text-yellow-600 text-sm'}>
            <Icon icon={'exclamation-triangle'} />{' '}
            {getErrorMessage(errorGetPatientInfo) || 'Cannot create biotwin from HIS.'}
          </span>
        </div>
      )}
    </div>
  );
};
interface NoBiotwinsFoundProps {
  className?: string;
  globalFilter: string;
}
const NoBiotwinsFound = ({className, globalFilter}: NoBiotwinsFoundProps) => {
  const tenantId = useAppSelector(selectTenantId);
  const {data: tenants = []} = useGetTenantsQuery(undefined, {
    skip: !tenantId || tenantId === 1,
  });
  const tenant = tenants.find((tenant) => tenant.id === tenantId);
  const usesIntegration = tenant?.usesIntegration;
  const [prevGlobalFilter, setPrevGlobalFilter] = useState(globalFilter);
  const [cannotCreateBiotwin, setCannotCreateBiotwin] = useState(false);
  const [cannotGetFromHIS, setCannotGetFromHIS] = useState(false);
  const [isCreatingBiotwin, setIsCreatingBiotwin] = useState(false);
  const [createBiotwin, {error: errorCreateBiotwin}] = useCreateBiotwinMutation();
  const [refreshToken] = useRefreshTokenMutation();
  const [getPatientInfo, {error: errorGetPatientInfo, isFetching: isFetchingGetPatientInfo}] =
    useLazyGetPatientInfoQuery();
  const navigate = useNavigate();
  if (prevGlobalFilter !== globalFilter) {
    setPrevGlobalFilter(globalFilter);
    setCannotGetFromHIS(false);
    setCannotCreateBiotwin(false);
  }
  return (
    <InfoSection
      className={className}
      content={
        <div className={'flex flex-col items-center gap-2'}>
          {!usesIntegration && (
            <Button
              className={cn('w-[12rem]', 'rounded-full')}
              disabled={cannotCreateBiotwin}
              isLoading={isCreatingBiotwin}
              onClick={async () => {
                try {
                  setIsCreatingBiotwin(true);
                  const biotwin = await createBiotwin({
                    name: globalFilter,
                    tenantId,
                  }).unwrap();
                  await refreshToken().unwrap();
                  setIsCreatingBiotwin(false);
                  navigate(`/biotwins/${biotwin.id}`);
                } catch (e) {
                  setCannotCreateBiotwin(true);
                  setIsCreatingBiotwin(false);
                }
              }}
              type="button"
              startIcon={<Icon icon={'plus'} />}>
              {'Create Now'}
            </Button>
          )}
          <div className={'relative'}>
            {usesIntegration && (
              <Button
                className={cn('w-[12rem]', 'rounded-full')}
                disabled={cannotGetFromHIS}
                isLoading={isFetchingGetPatientInfo}
                onClick={async () => {
                  try {
                    const patientInfo = await getPatientInfo({
                      patientId: globalFilter,
                    }).unwrap();
                    if (patientInfo.biotypeId) {
                      navigate(`/biotwins/${patientInfo.biotypeId}`);
                    }
                  } catch (e) {
                    setCannotGetFromHIS(true);
                  }
                }}
                type="button"
                startIcon={<Icon icon={'download'} />}>
                {'Create From HIS'}
              </Button>
            )}
            {!usesIntegration && cannotCreateBiotwin && (
              <div
                className={'absolute top-full left-1/2 flex justify-center mt-2 flex items-center whitespace-nowrap'}
                style={{
                  width: 0,
                }}>
                <span className={'text-yellow-600 text-sm'}>
                  <Icon icon={'exclamation-triangle'} />{' '}
                  {getErrorMessage(errorCreateBiotwin) || 'Cannot create biotwin.'}
                </span>
              </div>
            )}
            {usesIntegration && cannotGetFromHIS && (
              <div
                className={'absolute top-full left-1/2 flex justify-center mt-2 flex items-center whitespace-nowrap'}
                style={{
                  width: 0,
                }}>
                <span className={'text-yellow-600 text-sm'}>
                  <Icon icon={'exclamation-triangle'} />{' '}
                  {getErrorMessage(errorGetPatientInfo) || 'Cannot create biotype from HIS.'}
                </span>
              </div>
            )}
          </div>
        </div>
      }
      icon={'folder-open'}
      message={
        usesIntegration
          ? `Let Biotandem create a Biotwin for this person from the records.`
          : `You can create a new biotwin named ${globalFilter}.`
      }
      subtitle={usesIntegration ? 'There is no user-created Biotwin for this person yet.' : undefined}
      subtitleClassName={'text-muted-foreground mt-2'}
      title={'No Results Found'}
    />
  );
};
